import {
    escapeRegExp,
    formatClusterFilter,
    getFormatHealthStatFilter,
    removeDiacritics,
    removeNonAlphaNumeric,
} from './helpers';
import kebabCase from 'lodash/kebabCase';
import { tooltips } from '@/copy';

export const filterAddedOn = ({ addedOn }, addedOnFilters) => {
    return addedOnFilters.some((yearWeekStr) => yearWeekStr === addedOn);
};

export const filterClusters = ({ cluster }, clusters) => {
    return (
        !(clusters && clusters.length) ||
        (cluster === null &&
            clusters
                .map(({ value }) => value)
                .some((c) => c.includes('no cluster'))) ||
        clusters.map(({ value }) => value).includes(cluster)
    );
};

export const filterHealthStats = ({ healthStats }, filters) => {
    const healthStatFilters = filters
        ? filters.reduce((acc, { metricLabel, score, upperScoreThreshold }) => {
              const range = [score, upperScoreThreshold];
              if (acc[metricLabel]) {
                  acc[metricLabel].push(range);
              } else {
                  acc[metricLabel] = [range];
              }
              return acc;
          }, {})
        : null;

    if (!healthStatFilters) return true;
    if (!healthStats) return false;

    return Object.keys(healthStatFilters).every((metricLabel) => {
        return Object.entries(healthStats).some(
            ([cardMetricLabel, { score }]) => {
                return (
                    metricLabel === cardMetricLabel &&
                    (score || score === 0) &&
                    healthStatFilters[metricLabel].some(
                        ([lower, upper]) => score >= lower && score < upper,
                    )
                );
            },
        );
    });
};

export const filterParentName = ({ parentName }, parents) => {
    return (
        !(parents && parents.length) ||
        parents
            .map(({ label }) => label)
            .some((filterParentName) =>
                Array.isArray(parentName)
                    ? parentName.includes(filterParentName)
                    : filterParentName === parentName,
            )
    );
};

export const filterNames = (item, queries) => {
    const name = item.searchField || item.name;
    const normalized = removeDiacritics(name || '');
    const alphaNumeric = removeNonAlphaNumeric(normalized);
    return (
        !(name && queries && queries.length) ||
        queries.some((query) => {
            const queryExp = new RegExp(
                `(${escapeRegExp(query)})|(${removeNonAlphaNumeric(query)})`,
                'gi',
            );
            return queryExp.test(normalized) || queryExp.test(alphaNumeric);
        })
    );
};

export const filterIds = (item, list) => {
    const id = item.id;
    return !(id && list && list.length) || list.find((l) => l === id);
};

const isCat4Filter = (flavorFilter) => flavorFilter.depth === 4;
export const filterFlavors = (item, flavorsFilter) => {
    return flavorsFilter.every((flavorFilter) =>
        item?.hierarchies?.some(([id, hierarchy]) => {
            if (hierarchy.cat1 !== 'flavors') return false;
            if (isCat4Filter(flavorFilter)) {
                if (flavorFilter.catTwo !== kebabCase(hierarchy.cat2))
                    return false;
                if (flavorFilter.catThree !== kebabCase(hierarchy.cat3))
                    return false;
                if (
                    flavorFilter.value !==
                    kebabCase(
                        `${hierarchy.cat2}>${hierarchy.cat3}>${hierarchy.cat4}`,
                    )
                )
                    return false;
            } else {
                // cat3 filter
                if (flavorFilter.catTwo !== kebabCase(hierarchy.cat2))
                    return false;
                if (
                    flavorFilter.value !==
                        kebabCase(`${hierarchy.cat2}>${hierarchy.cat3}`) &&
                    flavorFilter.value !==
                        kebabCase(`all-${flavorFilter.catTwo}`)
                )
                    return false;
            }

            return true; // all checks passed, hierarchy matches this filter
        }),
    );
};

// used to generate flavor item counts without cat4 filtering
export const filterFlavorsWithoutCatFours = (item, flavorsFilter) => {
    return flavorsFilter.every((flavorFilter) =>
        item?.hierarchies?.some(([id, hierarchy]) => {
            if (hierarchy.cat1 !== 'flavors') return false;
            if (isCat4Filter(flavorFilter)) {
                if (flavorFilter.catTwo !== kebabCase(hierarchy.cat2))
                    return false;
                if (flavorFilter.catThree !== kebabCase(hierarchy.cat3))
                    return false;
            } else {
                // cat3 filter
                if (flavorFilter.catTwo !== kebabCase(hierarchy.cat2))
                    return false;
                if (
                    flavorFilter.value !==
                        kebabCase(`${hierarchy.cat2}>${hierarchy.cat3}`) &&
                    flavorFilter.value !==
                        kebabCase(`all-${flavorFilter.catTwo}`)
                )
                    return false;
            }

            return true;
        }),
    );
};

export const lifeCycleFilterOptions = [
    {
        value: 'early',
        label: 'Early',
        description: 'Speculative, nascent trends',
    },
    {
        value: 'growth',
        label: 'Growth',
        description: 'Rising trends to watch',
    },
    {
        value: 'mainstream',
        label: 'Mainstream',
        description: 'High awareness category drivers',
    },
    {
        value: 'fade',
        label: 'Fade',
        description: 'Volume driven by habit, slowing down',
    },
];

export const filterLifeCycle = ({ life_cycle }, lifeCycleFilters) => {
    return (
        !(lifeCycleFilters && lifeCycleFilters.length) ||
        lifeCycleFilters.map(({ value }) => value).includes(life_cycle)
    );
};

export const clusterFilterOptions = tooltips.clusters
    .filter(
        ({ value }) =>
            !value.includes('tiktok') && !value.includes('instagram'),
    )
    .map(formatClusterFilter);

export const tikTokClusterFilterOptions = tooltips.clusters
    .filter(({ value }) => value.includes('tiktok'))
    .map(formatClusterFilter);

export const instagramClusterFilterOptions = tooltips.clusters
    .filter(({ value }) => value.includes('instagram'))
    .map(formatClusterFilter);

const PRICE_POINT = 'price point';

export const healthStatFilterOptions = Object.entries(tooltips.healthStats)
    .map(([metricLabel, { abbrTitleHTML, scoreLabels, scoreDefinitions }]) => {
        const formatter = getFormatHealthStatFilter(metricLabel, {
            scoreLabels,
            scoreDefinitions,
        });
        return {
            label: metricLabel,
            abbrTitleHTML,
            panelId: kebabCase(metricLabel),
            filter: 'healthStats',
            options: Object.keys(scoreLabels)
                .map((score) => parseInt(score, 10))
                .sort((a, b) => b - a)
                .map((scoreThreshold, idx, ref) =>
                    formatter(scoreThreshold, idx > 0 ? ref[idx - 1] : 11),
                ),
        };
    })
    // Every menu filters this list, and filter and sort are stable, so
    // ordering price point last here orders it last in all of them.
    .sort((a, b) => {
        if (a.label === b.label) return 0;
        if (a.label === PRICE_POINT) return 1;
        if (b.label === PRICE_POINT) return -1;
        return 0;
    });
